import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell } from "lucide-react";
import { subscribeToPush } from "@/lib/push";

export const NotificationBell = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [swing, setSwing] = useState(false);
  const prevCount = useRef(0);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread_count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 5000);
    return () => clearInterval(id);
  }, [loadCount]);

  // Fait osciller la cloche quand une nouvelle notif arrive (count augmente)
  useEffect(() => {
    if (count > prevCount.current && count > 0) {
      setSwing(true);
      const to = setTimeout(() => setSwing(false), 1000);
      prevCount.current = count;
      return () => clearTimeout(to);
    }
    prevCount.current = count;
  }, [count]);

  const open = (o) => {
    setMenuOpen(o);
    if (o) {
      subscribeToPush(true); // geste utilisateur -> demande l'autorisation d'alertes
      api.get("/notifications").then((r) => setItems(r.data));
      api.post("/notifications/read_all").then(() => setCount(0));
    }
  };

  const openNotif = (n) => {
    setMenuOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={menuOpen} onOpenChange={open}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notification-bell">
          <Bell className={`h-5 w-5 transition-colors ${count > 0 ? "text-primary" : ""} ${swing ? "animate-bell-swing" : ""}`} />
          {count > 0 && (
            <>
              {swing && <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary/60 pointer-events-none animate-badge-ping" aria-hidden="true" />}
              <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold pointer-events-none" data-testid="notification-count">
                {count > 9 ? "9+" : count}
              </span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="px-4 py-2.5 border-b border-border font-heading font-semibold text-secondary sticky top-0 bg-card">
          {t("notif.title")}
        </div>
        {items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t("notif.empty")}</p>
        ) : (
          items.map((n) => (
            <button key={n.id} onClick={() => openNotif(n)} data-testid={`notif-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition-colors ${n.link ? "cursor-pointer" : "cursor-default"} ${!n.read ? "bg-primary/5" : ""}`}>
              <p className={`text-sm flex items-center gap-1.5 ${!n.read ? "font-bold text-secondary" : "font-normal text-muted-foreground"}`}>
                {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" aria-hidden="true" />}
                {n.title}
              </p>
              <p className={`text-xs line-clamp-2 ${!n.read ? "text-foreground/80" : "text-muted-foreground"}`}>{n.body}</p>
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
